import { useCallback, useEffect, useRef } from 'react';
import { localStorageTokenKey, readLocalStorage, useLocalStorage } from 'shared/hooks';
import { parseJwt } from 'shared/utils';
import { PROMPT_MINUTES, SESSION_CHECK_INTERVAL } from '../constants';
var PROMPT_SECONDS = PROMPT_MINUTES * 60;
var ModalDisabledLocalStorageKey = 'session-modal-disabled';
var useLocalStorageForSessionLogout = function () {
    var _a = useLocalStorage(ModalDisabledLocalStorageKey, false), isModalDisabledLocalStorage = _a[0], setIsModalDisabledLocalStorage = _a[1];
    var getTokenFromLocalStorage = useCallback(readLocalStorage(localStorageTokenKey, null, false), [useCallback]);
    return {
        isModalDisabledLocalStorage: isModalDisabledLocalStorage,
        setIsModalDisabledLocalStorage: setIsModalDisabledLocalStorage,
        getTokenFromLocalStorage: getTokenFromLocalStorage,
    };
};
export var useSessionTimer = function (disclosure, logout) {
    var onClose = disclosure.onClose, onOpen = disclosure.onOpen;
    var _a = useLocalStorageForSessionLogout(), isModalDisabledLocalStorage = _a.isModalDisabledLocalStorage, setIsModalDisabledLocalStorage = _a.setIsModalDisabledLocalStorage, getTokenFromLocalStorage = _a.getTokenFromLocalStorage;
    // use references to not trigger re-render on update
    var remainingTime = useRef(-1);
    var intervalRef = useRef(null);
    var showModalRef = useRef(!isModalDisabledLocalStorage);
    // Stop interval and reset all references
    var stopIntervalAndClean = function () {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            setIsModalDisabledLocalStorage(false);
            intervalRef.current = null;
            showModalRef.current = false;
        }
    };
    // disable modal when the user clicks 'acknowledge', prevents the modal to show again but keep checking for token exp (logout)
    var disableModal = function () {
        showModalRef.current = false;
        // Persist acknowledge
        setIsModalDisabledLocalStorage(true);
    };
    var storageListener = function (event) {
        var key = event.key, newValue = event.newValue, oldValue = event.oldValue;
        // check for acknowledge/disabled flag, used to sync between tabs
        if (key === ModalDisabledLocalStorageKey && newValue !== oldValue) {
            // if is disabled, close modal and set flag to false, so it doesn't opens again
            if (newValue === 'true') {
                showModalRef.current = false;
                disclosure.onClose();
            }
            else
                showModalRef.current = true;
        }
        // check for token changes
        if (key === localStorageTokenKey && newValue !== oldValue) {
            if (newValue === null || newValue === 'undefined')
                stopIntervalAndClean();
            else
                intervalRef.current = setInterval(validateTokenExp, SESSION_CHECK_INTERVAL);
        }
    };
    var validateTokenExp = useCallback(function () {
        // check token every x seconds
        var tokenValue = getTokenFromLocalStorage();
        var token = parseJwt(tokenValue !== null && tokenValue !== void 0 ? tokenValue : '');
        // Get current time in seconds (jwt exp is in seconds)
        var now = Date.now().valueOf() / 1000;
        if (token) {
            // If expiration is before current time, logout and stop checking
            if (token.exp <= now) {
                stopIntervalAndClean();
                onClose();
                logout();
            }
            else if (showModalRef.current && token.exp - PROMPT_SECONDS <= now) {
                // set remaining time (in miliseconds)
                remainingTime.current = (token.exp - now) * 1000;
                onOpen();
            }
        }
        else {
            // if token doesn't exists stop checking
            stopIntervalAndClean();
        }
    }, [getTokenFromLocalStorage]);
    useEffect(function () {
        // start checking on mount
        intervalRef.current = setInterval(validateTokenExp, SESSION_CHECK_INTERVAL);
        window.addEventListener('storage', storageListener);
        // if component gets unmounted, stop
        return function () {
            stopIntervalAndClean();
            window.removeEventListener('storage', storageListener);
        };
    }, []);
    return { remainingTime: remainingTime.current, disableModal: disableModal };
};
